import { Button } from '@mui/material'
import { Alert, Progress, message } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import io from 'socket.io-client'
import store from 'store2'

import { JWT_TOKEN_NAME } from '../../../../../constants/auth-key'
import { ONLY_DATE_TIME_FORMAT } from '../../../../../constants/global'
import { useUser } from '../../../../../hooks/useUser'
import { numberFormat } from '../../../../../utils'
import { useStockBackfillStatus } from '../hooks/useStockBackfillStatus'
import { IStockBackfillStatus } from '../types'
import StockBackfillModal from './StockBackfillModal'

interface Props {
  shopId?: string
  shopName?: string
  // ชื่อร้านตาม shopId — job มีรอบเดียวต่อ merchant อาจเป็นของร้านอื่นที่ไม่ได้เลือกอยู่
  shopNames?: Record<string, string>
  // เรียกหลังตัดย้อนหลังเสร็จ เพื่อให้รายงานตัดสต๊อกโหลดใหม่
  onCompleted?: () => void
}

const lastRunSummary = (status: IStockBackfillStatus) =>
  [
    `ตัดแล้ว ${numberFormat(status.deducted?.quantity ?? 0, 0)} ชิ้น`,
    status.cancelled?.quantity
      ? `ปิดของออเดอร์ที่ยกเลิก ${numberFormat(
          status.cancelled.quantity,
          0,
        )} ชิ้น`
      : '',
    status.skipped?.quantity
      ? `ยังตัดไม่ได้ ${numberFormat(status.skipped.quantity, 0)} ชิ้น`
      : '',
    status.failed?.quantity
      ? `ตัดไม่สำเร็จ ${numberFormat(
          status.failed.quantity,
          0,
        )} ชิ้น (กดใหม่ได้)`
      : '',
  ]
    .filter(Boolean)
    .join(' · ')

// ตัดสต๊อกย้อนหลังให้ชิ้นที่ TikTok สั่งตัดมาตอนร้านยังไม่ผูก SKU
// ทำงานเบื้องหลัง — สถานะจาก BE (ทน refresh) + socket แจ้งตอนเปิดหน้าค้างไว้
const StockBackfillPanel = ({
  shopId,
  shopName,
  shopNames,
  onCompleted,
}: Props) => {
  const { merchant } = useUser()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const username = merchant?.prefix

  const { status, isRunning, pendingOrders, refetch } = useStockBackfillStatus(
    shopId,
    onCompleted,
  )

  const handleCompleted = useCallback(() => {
    onCompleted?.()
    refetch()
  }, [onCompleted, refetch])

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_SERVICE
    if (!username || !socketUrl) {
      return
    }

    const jwtToken = store.get(JWT_TOKEN_NAME)
    const socket = io(socketUrl, {
      transports: ['websocket'],
      path: '/ws/',
      reconnection: true,
      query: { token: jwtToken ?? '' },
    })

    const subscribe = () => socket.emit('subscribe', { username })
    subscribe()
    socket.on('connect', subscribe)

    socket.on(
      'order:backfill-stock:completed',
      (data: { deducted?: { quantity?: number } }) => {
        message.success(
          `ตัดสต๊อกย้อนหลังเสร็จแล้ว ${numberFormat(
            data?.deducted?.quantity ?? 0,
            0,
          )} ชิ้น`,
        )
        handleCompleted()
      },
    )
    socket.on('order:backfill-stock:failed', (data: { error?: string }) => {
      message.error(
        `ตัดสต๊อกย้อนหลังล้มเหลว: ${data?.error ?? 'ไม่ทราบสาเหตุ'}`,
      )
      refetch()
    })

    return () => {
      socket.off()
      socket.disconnect()
    }
  }, [username, handleCompleted, refetch])

  const scope = status?.shopId
    ? `ร้าน ${shopNames?.[status.shopId] ?? status.shopId}`
    : 'ทุกร้าน'

  const percent = status?.totalOrders
    ? Math.min(
        Math.round((status.processedOrders / status.totalOrders) * 100),
        100,
      )
    : 0

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: isRunning || status?.status ? 8 : 0,
        }}
      >
        {!isRunning && pendingOrders > 0 && (
          <span style={{ color: '#8c8c8c', fontSize: 13 }}>
            มี {numberFormat(pendingOrders, 0)} คำสั่งซื้อที่สต๊อกยังไม่ถูกตัด
          </span>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={() => setModalOpen(true)}
          disabled={isRunning || pendingOrders === 0}
        >
          ตัดสต๊อกย้อนหลัง
        </Button>
      </div>

      {isRunning && status && (
        <Alert
          type="info"
          showIcon
          message={`กำลังตัดสต๊อกย้อนหลัง (${scope}) ${numberFormat(
            status.processedOrders,
            0,
          )}/${numberFormat(status.totalOrders, 0)} คำสั่งซื้อ`}
          description={
            <>
              <Progress percent={percent} size="small" />
              {status.startedAt &&
                `เริ่มเมื่อ ${dayjs(status.startedAt).format(
                  ONLY_DATE_TIME_FORMAT,
                )} — ปิดหน้านี้ได้ ระบบทำต่อจนเสร็จ`}
            </>
          }
        />
      )}

      {!isRunning && status?.status === 'COMPLETED' && (
        <Alert
          type="success"
          showIcon
          message={`ตัดสต๊อกย้อนหลังรอบล่าสุด (${scope})${
            status.completedAt
              ? ` (${dayjs(status.completedAt).format(ONLY_DATE_TIME_FORMAT)})`
              : ''
          }: ${lastRunSummary(status)}`}
        />
      )}

      {!isRunning && status?.status === 'FAILED' && (
        <Alert
          type="error"
          showIcon
          message={`ตัดสต๊อกย้อนหลังรอบล่าสุด (${scope}) ไม่สำเร็จ: ${
            status.error ?? 'ไม่ทราบสาเหตุ'
          } — ${lastRunSummary(status)}`}
        />
      )}

      <StockBackfillModal
        open={modalOpen}
        shopId={shopId}
        shopName={shopName}
        onClose={() => setModalOpen(false)}
        onStarted={() => {
          message.info('เริ่มตัดสต๊อกย้อนหลังแล้ว จะแจ้งเตือนเมื่อเสร็จ')
          refetch()
        }}
      />
    </div>
  )
}

export default StockBackfillPanel
